"use client";
import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import CounterAnimation from "./CounterAnimation";

const stats = [
  { number: 1500, suffix: "+", label: "حملة اعلانية ناجحة" },
  { number: 500, suffix: "+", label: "شاشات ولوحات" },
  { number: 7, suffix: "+", label: "وكلاء حصريين في اكبر المراكز التجارية" },
  { number: 98, suffix: "+", label: "ادراة مواقع التواصل الاجتماعي" },
];

const workSteps = [
  {
    step: "01",
    title: "الاكتشاف والاستراتيجية",
    description:
      "فهم الأهداف والجمهور المستهدف والقيمة المضافة الفريدة لعلامتك التجارية. ووضع استراتيجية شاملة تتوافق مع أهدافك.",
    // icon: "🎯",
    color: "from-blue-500 to-purple-600",
  },
  {
    step: "02",
    title: "تطوير الأفكار الإبداعية",
    description:
      "تفكير جماعي لاستحضار أفكار مبتكرة وصياغة روايات مقنعة وتصميم صور جذابة تلامس جمهورك.",
    // icon: "💡",
    color: "from-yellow-500 to-orange-600",
  },
  {
    step: "03",
    title: "تنفيذ الحملة",
    description:
      "تنفيذ نهج متعدد القنوات، باستخدام وسائل التواصل الاجتماعي وتحسين محركات البحث والتسويق بالمحتوى والإعلان المدفوع لتحقيق أقصى قدر من التغطية والتحويلات.",
    // icon: "🚀",
    color: "from-green-500 to-teal-600",
  },
  {
    step: "04",
    title: "القياس وتحليل البيانات",
    description:
      "مراقبة الأداء وتتبع المؤشرات واستخدام البيانات لاتخاذ القرارات المستندة إلى البيانات وتحسين الاستراتيجيات.",
    // icon: "📊",
    color: "from-purple-500 to-pink-600",
  },
];

const StatsSection = ({ data }) => {
  // Helper to extract string from localized object or return string
  const getLocalizedValue = (value, fallback = "") => {
    if (!value) return fallback;
    if (typeof value === "string") return value;
    if (typeof value === "object" && value !== null) {
      // If it's a localized object {en, ar}, use Arabic (ar) for Arabic page
      return value.ar || value.en || fallback;
    }
    return fallback;
  };

  // Fallback to hardcoded values if data is not available
  const title = getLocalizedValue(data?.title, "إنجازاتنا بالأرقام");
  const subtitle = getLocalizedValue(data?.subtitle, "نفتخر بما حققناه من نجاحات");
  const achievements = Array.isArray(data?.items) && data.items.length > 0
    ? data.items.map((item) => ({
        ...item,
        label: getLocalizedValue(item.label, item.label || ""),
      }))
    : stats;

  const statsRef = useRef(null);
  const workRef = useRef(null);
  const isStatsInView = useInView(statsRef, { once: true });
  const isWorkInView = useInView(workRef, { once: true });

  return (
    <section className="lg:py-20 pt-10 text-[#233c78] overflow-hidden relative">
      {/* Animated Background */}
      <div className="absolute inset-0">
        <motion.div
          className="absolute top-0 right-0 w-96 h-96 bg-[#dfa428]/10 rounded-full"
          animate={{
            scale: [1, 1.3, 1],
            rotate: [0, 180, 360],
            x: [0, 100, 0],
          }}
          transition={{ duration: 20, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-0 left-0 w-80 h-80 bg-white/5 rounded-full"
          animate={{
            scale: [1.2, 1, 1.2],
            rotate: [360, 180, 0],
            y: [0, -50, 0],
          }}
          transition={{ duration: 15, repeat: Infinity }}
        />
      </div>

      {/* Stats */}
      <div
        ref={statsRef}
        className="container mx-auto px-6 mb-32 relative z-10"
      >
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          animate={isStatsInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-5xl font-bold mb-6">{title}</h2>
          <p className="text-xl opacity-90">{subtitle}</p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {achievements.map((stat, index) => (
            <motion.div
              key={index}
              className="text-center"
              initial={{ opacity: 0, y: 100 }}
              animate={isStatsInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: index * 0.2 }}
            >
              <div className="bg-white/10  backdrop-blur-sm rounded-3xl p-8 hover:bg-white/20 transition-all duration-300">
                <CounterAnimation
                  target={stat.value || stat.number}
                  suffix={stat.suffix || "+"}
                  label={stat.label || ""}
                  duration={2.5}
                />
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Enhanced Work Process Timeline */}
      {/* <div ref={workRef} className="container mx-auto px-6 relative z-10">
        <motion.div
          className="text-center mb-20"
          initial={{ opacity: 0, y: 50 }}
          animate={isWorkInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-5xl font-bold mb-6">مسار العمل</h2>
          <p className="text-xl opacity-90">كيف نعمل</p>
        </motion.div> */}

      {/* Creative Timeline Container */}
      {/* <div className="relative max-w-6xl mx-auto"> */}
      {/* Animated Central Path */}
      {/* <motion.svg
            className="absolute inset-0 w-full h-full"
            viewBox="0 0 800 600"
            initial={{ pathLength: 0 }}
            animate={isWorkInView ? { pathLength: 1 } : {}}
            transition={{ duration: 3, delay: 0.5 }}
          >
            <motion.path
              d="M 100 100 Q 400 50 700 150 Q 400 250 100 350 Q 400 450 700 550"
              stroke="url(#gradient)"
              strokeWidth="3"
              fill="none"
              strokeDasharray="10,5"
            />
            <defs>
              <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#fbbf24" />
                <stop offset="50%" stopColor="#ffffff" />
                <stop offset="100%" stopColor="#fbbf24" />
              </linearGradient>
            </defs>
          </motion.svg> */}

      {/* Timeline Steps */}
      {/* <div className="relative grid grid-cols-1 md:grid-cols-2 gap-16 py-16">
            {workSteps.map((step, index) => (
              <EnhancedWorkStepCard
                key={index}
                step={step}
                index={index}
                isInView={isWorkInView}
                isLeft={index % 2 === 0}
              />
            ))}
          </div> */}
      {/* </div> */}
      {/* </div> */}
    </section>
  );
};

const EnhancedWorkStepCard = ({ step, index, isInView, isLeft }) => {
  return (
    <motion.div
      className={`relative ${
        isLeft
          ? "md:justify-self-end md:text-right"
          : "md:justify-self-start md:text-left"
      }`}
      initial={{
        opacity: 0,
        x: isLeft ? -300 : 300,
        rotateY: isLeft ? -45 : 45,
      }}
      animate={isInView ? { opacity: 1, x: 0, rotateY: 0 } : {}}
      transition={{
        duration: 1.2,
        delay: index * 0.4,
        type: "spring",
        stiffness: 100,
      }}
    >
      {/* Floating Timeline Node */}
      <motion.div
        className={`absolute ${
          isLeft ? "md:-left-8" : "md:-right-8"
        } top-8 w-16 h-16 bg-gradient-to-br ${
          step.color
        } rounded-full z-20 shadow-2xl flex items-center justify-center`}
        initial={{ scale: 0, rotate: -180 }}
        animate={isInView ? { scale: 1, rotate: 0 } : {}}
        transition={{ duration: 0.8, delay: index * 0.4 + 0.5, type: "spring" }}
        whileHover={{ scale: 1.2, rotate: 360 }}
      >
        <span className="text-2xl">{step.icon}</span>
      </motion.div>

      {/* Enhanced Card */}
      <motion.div
        className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 hover:bg-white/20 transition-all duration-500 group relative overflow-hidden border border-white/20"
        whileHover={{
          scale: 1.05,
          y: -10,
          rotateY: isLeft ? 5 : -5,
          transition: { duration: 0.3 },
        }}
      >
        {/* Animated Background Gradient */}
        <motion.div
          className={`absolute inset-0 bg-gradient-to-br ${step.color} opacity-0 group-hover:opacity-20 transition-opacity duration-500 rounded-3xl`}
          initial={false}
        />

        {/* Floating Particles */}
        <motion.div
          className="absolute top-4 right-4 w-2 h-2 bg-[#dfa428]/60 rounded-full"
          animate={{
            scale: [1, 2, 1],
            opacity: [0.6, 1, 0.6],
            x: [0, 10, 0],
            y: [0, -10, 0],
          }}
          transition={{ duration: 3, repeat: Infinity, delay: index * 0.5 }}
        />
        <motion.div
          className="absolute bottom-4 left-4 w-3 h-3 bg-white/40 rounded-full"
          animate={{
            scale: [2, 1, 2],
            opacity: [0.4, 0.8, 0.4],
            rotate: [0, 180, 360],
          }}
          transition={{ duration: 4, repeat: Infinity, delay: index * 0.3 }}
        />

        {/* Content */}
        {/* <div className="relative z-10">
          <div className="flex items-center space-x-4 space-x-reverse mb-6">
            <motion.div
              className="bg-[#dfa428] text-[#0a367b] w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold flex-shrink-0 shadow-lg"
              whileHover={{ rotate: 360, scale: 1.1 }}
              transition={{ duration: 0.5 }}
            >
              {step.step}
            </motion.div>
            <motion.div
              className="text-xl"
              animate={{
                rotate: [0, 15, -15, 0],
                scale: [1, 1.1, 1],
              }}
              transition={{ duration: 4, repeat: Infinity, delay: index * 0.8 }}
            >
              {step.icon}
            </motion.div>
          </div>

          <motion.h3
            className="text-2xl font-bold mb-4 group-hover:text-[#dfa428] transition-colors duration-300"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: index * 0.4 + 0.7 }}
          >
            {step.title}
          </motion.h3>

          <motion.p
            className="text-lg opacity-90 leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: index * 0.4 + 0.9 }}
          >
            {step.description}
          </motion.p>
        </div> */}

        {/* Connecting Energy Line */}
        {/* <motion.div
          className={`absolute top-1/2 ${
            isLeft ? "right-0" : "left-0"
          } w-20 h-0.5 bg-gradient-to-${
            isLeft ? "r" : "l"
          } from-[#dfa428] via-white to-transparent`}
          initial={{ scaleX: 0, opacity: 0 }}
          animate={isInView ? { scaleX: 1, opacity: 1 } : {}}
          transition={{ duration: 1, delay: index * 0.4 + 1.2 }}
        /> */}

        {/* Glow Effect */}
        {/* <motion.div
          className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          style={{
            background: `linear-gradient(45deg, transparent, rgba(255, 193, 7, 0.1), transparent)`,
            filter: "blur(20px)",
          }}
        /> */}
      </motion.div>
    </motion.div>
  );
};

export default StatsSection;
